import { AxiosRequestConfig, AxiosResponse } from 'axios';
import querystring from 'querystring';

import {
  parseClient,
  parseAccessToken,
  parseAuthN,
  parseAuthZ,
  parseMediaToken,
} from 'models/adobe';
import {
  HttpClient,
  QueryOptions,
  AdobeClientResponse,
  AdobeAccessTokenResponse,
  AdobeAuthResponse,
  AdobeMediaTokenResponse,
} from './api-types';

interface TveExtraOptions {
  params?: Record<string, string>;
  headers?: Record<string, string>;
}

// Note: All CBS Sports APIs use production Adobe APIs, including QA
//       Base Url from kitchen sink has a trailing '/'
const API_HOST_NAME = 'https://api.auth.adobe.com/';
const API_CLIENT_PATH = 'o/client';
const API_TVE_PATH = 'api/v1';
const REGISTER_PATH = 'register';
const ACCESS_TOKEN_PATH = 'token';
const AUTHN_TOKEN_PATH = 'tokens/authn';
const AUTHORIZE_PATH = 'authorize';
const MEDIA_TOKEN_PATH = 'tokens/media';
const LOGOUT_PATH = 'logout';

const SOFTWARE_STATEMENT = import.meta.env.VITE_ADOBE_SOFTWARE_STATEMENT;

const queryApi = (
  httpClient: HttpClient,
  url: string,
  options: QueryOptions,
): Promise<AxiosResponse> => {
  const request: AxiosRequestConfig = {
    url,
    method: options.method || (options && options.data ? 'post' : 'get'),
  };

  if (options) {
    if (options.params) request.params = options.params;
    request.headers = options.headers || {};
    if (options.data) {
      if (options.contentType === 'JSON') {
        request.data = JSON.stringify(options.data);
        request.headers = {
          ...request.headers,
          'content-type': 'application/json',
        };
      } else {
        request.data = querystring.stringify(
          options.data as querystring.ParsedUrlQueryInput,
        );
        request.headers = {
          ...request.headers,
          'content-type': 'application/x-www-form-urlencoded',
        };
      }
    }
  }

  return httpClient(request);
};

const getClientUrlPath = (host: string | undefined, target: string): string => {
  return `${host || API_HOST_NAME}${API_CLIENT_PATH}/${target}`;
};

const getTveUrlPath = (host: string | undefined, target: string): string => {
  return `${host || API_HOST_NAME}${API_TVE_PATH}/${target}`;
};

export const fetchClientInfo = async (
  httpClient: HttpClient,
  host?: string,
): Promise<AdobeClientResponse> => {
  const url = getClientUrlPath(host, REGISTER_PATH);
  const params = { software_statement: SOFTWARE_STATEMENT };

  const response = await queryApi(httpClient, url, {
    data: params,
    contentType: 'JSON',
  });
  const result = parseClient(response.data);
  if (!result.id || !result.secret) {
    throw new Error('Missing Client Information');
  }

  return result;
};

export const fetchAccessToken = async (
  httpClient: HttpClient,
  host: string | undefined,
  clientId: string,
  clientSecret: string,
): Promise<AdobeAccessTokenResponse> => {
  const url = getClientUrlPath(host, ACCESS_TOKEN_PATH);
  const params = {
    grant_type: 'client_credentials',
    client_id: clientId,
    client_secret: clientSecret,
  };

  const response = await queryApi(httpClient, url, {
    data: params,
  });
  const result = parseAccessToken(response.data);
  if (!result.token) throw new Error('Missing Token Information');

  return result;
};

const getAuthorizationHeader = (
  accessToken: string,
): Record<string, string> => {
  return { Authorization: `Bearer ${accessToken}` };
};

const getTveOptions = (
  accessToken: string,
  deviceId: string,
  extraOptions: TveExtraOptions = {},
): QueryOptions => {
  return {
    params: {
      requestor: 'CBS_SPORTS', // TODO: Use authN requestor value?  Internal/External config?
      deviceId,
      ...extraOptions.params,
    },
    headers: {
      ...getAuthorizationHeader(accessToken),
      ...extraOptions.headers,
    },
  };
};

export const fetchAuthN = async (
  httpClient: HttpClient,
  host: string | undefined,
  accessToken: string,
  deviceId: string,
): Promise<AdobeAuthResponse> => {
  const url = getTveUrlPath(host, AUTHN_TOKEN_PATH);
  const options = await getTveOptions(accessToken, deviceId);
  const response = await queryApi(httpClient, url, options);

  return parseAuthN(response.data);
};

export const fetchAuthZ = async (
  httpClient: HttpClient,
  host: string | undefined,
  accessToken: string,
  deviceId: string,
  resourceId: string,
): Promise<AdobeAuthResponse> => {
  const url = getTveUrlPath(host, AUTHORIZE_PATH);
  const options = await getTveOptions(accessToken, deviceId, {
    params: { resource: resourceId },
  });
  const response = await queryApi(httpClient, url, options);

  return parseAuthZ(response.data);
};

export const fetchMediaToken = async (
  httpClient: HttpClient,
  host: string | undefined,
  accessToken: string,
  deviceId: string,
  resourceId: string,
): Promise<AdobeMediaTokenResponse> => {
  const url = getTveUrlPath(host, MEDIA_TOKEN_PATH);
  const options = await getTveOptions(accessToken, deviceId, {
    params: { resource: resourceId },
  });
  const response = await queryApi(httpClient, url, options);

  return parseMediaToken(response.data);
};

export const logout = async (
  httpClient: HttpClient,
  host: string | undefined,
  accessToken: string,
  deviceId: string,
): Promise<boolean> => {
  const url = getTveUrlPath(host, LOGOUT_PATH);

  const options: QueryOptions = {
    method: 'delete',
    params: { deviceId },
    headers: getAuthorizationHeader(accessToken),
  };

  await queryApi(httpClient, url, options);
  return true;
};
